import { RemovableRef, useSessionStorage } from '@vueuse/core';
import { defineStore } from 'pinia';

import api from '@/api/api';
import { CREDIT_CARDS_HEADERS } from '@/constants/constants';
import { BROKER_CARDS_BASE_URL } from '@/constants/urls';
import { LOG_PREFIX } from '@/types/enums';
import { SmartSearchResponse } from '@/types/responseTypes';
import type { CardGoal } from '@/types/types';
import { transformCardDetails } from '@/utils/helperFunctions';

export default defineStore('creditCards', {
  state: () => ({
    cardDetails: {
      balanceTransferAmount: useSessionStorage('balanceTransferAmount', ''),
      immediateCashWithdrawal: useSessionStorage<boolean | null>('immediateCashWithdrawal', null),
    },
    cardIntent: useSessionStorage<string | null>('cardIntent', null),
    goalCode: useSessionStorage('goalCode', 'CC_BALTRANSFERV2') as RemovableRef<CardGoal>,
    enquiryInProgress: false,
    hasEnquiryError: false,
    hasResultsRequestCompleted: false,
    enquiryId: '',
    pollCount: 0,
    pollingTimedOut: false,
    isPrePopulated: false,
    resultsQueryEnabled: false,
  }),
  actions: {
    setEnquiryId(enquiryId: string) {
      this.enquiryId = enquiryId;
    },
    setCardDetails(cardDetails: { balanceTransferAmount: string; immediateCashWithdrawal: null }) {
      this.cardDetails = cardDetails;
    },
    setGoalCode(goalCode: CardGoal) {
      this.goalCode = goalCode;
    },
    setHasResultsRequestCompleted(hasResultsRequestCompleted: boolean) {
      this.hasResultsRequestCompleted = hasResultsRequestCompleted;
    },
    setPollCount(pollCount: number) {
      this.pollCount = pollCount;
    },
    setPollingTimedOut(pollingTimedOut: boolean) {
      this.pollingTimedOut = pollingTimedOut;
    },
    setIsPrePopulated(isPrePopulated: boolean) {
      this.isPrePopulated = isPrePopulated;
    },
    setResultsQueryEnabled(resultsQueryEnabled: boolean) {
      this.resultsQueryEnabled = resultsQueryEnabled;
    },
    clearSession() {
      this.cardDetails.balanceTransferAmount = '';
      this.cardDetails.immediateCashWithdrawal = null;
      this.goalCode = 'CC_BALTRANSFERV2';
      this.cardIntent = null;
    },
    async getEnquiry() {
      this.hasEnquiryError = false;
      this.enquiryInProgress = true;
      const cardDetails = transformCardDetails(this.cardDetails);
      const request = {
        settings: { resultsEmail: true },
        'smart-search': {
          userAgent: 'user-agent',
          user: {},
          goalCode: this.goalCode,
          payload: {
            customerDetails: cardDetails,
          },
        },
      };
      try {
        const { data } = await api<SmartSearchResponse>({
          url: `${BROKER_CARDS_BASE_URL}/smart-search`,
          method: 'post',
          data: request,
          headers: CREDIT_CARDS_HEADERS,
          logPrefix: `${LOG_PREFIX.CARDS} ENQUIRY`,
        });

        this.setEnquiryId(data.enquiryId);
        return data;
      } catch (error) {
        this.hasEnquiryError = true;
        return null;
      } finally {
        this.enquiryInProgress = false;
      }
    },
  },
});
